import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/** Where a charge stands. The provider's own wording never reaches the wire. */
export const PaymentChargeStatusSchema = z.enum([
  "pending",
  "paid",
  "expired",
  "canceled",
  "refunded",
  "failed",
]);
export type PaymentChargeStatus = z.infer<typeof PaymentChargeStatusSchema>;

/** How a customer pays. PIX and domestic cards only; amounts are always BRL centavos. */
export const PaymentMethodSchema = z.enum(["pix", "credit_card"]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

/** A Brazilian tax id: a person's CPF or a company's CNPJ. */
export const TaxIdTypeSchema = z.enum(["CPF", "CNPJ"]);
export type TaxIdType = z.infer<typeof TaxIdTypeSchema>;

/**
 * Machine-readable code for a refusal to charge because the payer's CPF or CNPJ is missing.
 * Clients read it to show the tax id field instead of a generic failure.
 */
export const PAYER_IDENTIFICATION_REQUIRED_CODE = "payer_identification_required";

/**
 * Normalizes a Brazilian tax id and checks its digits. Returns null for anything that is not
 * a valid CPF or CNPJ, so an invalid id never reaches a provider.
 */
export function normalizeBrazilianTaxId(value: string): { type: TaxIdType; number: string } | null {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    if (/^(\d)\1{10}$/.test(digits)) return null;
    return cpfIsValid(digits) ? { type: "CPF", number: digits } : null;
  }
  if (digits.length === 14) {
    if (/^(\d)\1{13}$/.test(digits)) return null;
    return cnpjIsValid(digits) ? { type: "CNPJ", number: digits } : null;
  }
  return null;
}

function cpfIsValid(digits: string): boolean {
  const first = checkDigit(digits.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = checkDigit(digits.slice(0, 9) + first, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return first === Number(digits[9]) && second === Number(digits[10]);
}

function cnpjIsValid(digits: string): boolean {
  const first = checkDigit(digits.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = checkDigit(digits.slice(0, 12) + first, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return first === Number(digits[12]) && second === Number(digits[13]);
}

function checkDigit(digits: string, weights: number[]): number {
  const sum = weights.reduce((total, weight, index) => total + Number(digits[index]) * weight, 0);
  const remainder = (sum * 10) % 11;
  return remainder === 10 ? 0 : remainder;
}

/** The card fields the provider's own front-end tokenization produces. */
export const CardTokenFieldsSchema = z.object({
  /** The token the provider's card form produced. The card number never reaches the server. */
  cardToken: z
    .string()
    .trim()
    .min(6)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/, "Use the provider's card token"),
  /**
   * The card's network as the provider's card form reported it, e.g. "visa". The server never
   * guesses it.
   */
  paymentMethodId: z
    .string()
    .trim()
    .min(2)
    .max(32)
    .regex(/^[a-z_]+$/, "Use the provider's payment method"),
  /** The issuing bank, when the card form reported one. */
  issuerId: z.string().trim().min(1).max(32).optional(),
});

/** A credit pack: how much model credit it is, and what it costs in BRL. */
export const CreditPackSchema = z.object({
  /** The pack's size, in US cents of model credit. */
  usdCents: z.number().int().positive(),
  /** What the pack costs, in BRL centavos. */
  brlCents: z.number().int().positive(),
});
export type CreditPack = z.infer<typeof CreditPackSchema>;

/**
 * What the deployment offers for paying. The public key is public by design: it only lets a
 * browser tokenize a card, and it never grants access to an account.
 */
export const PaymentConfigSchema = z.object({
  methods: z.array(PaymentMethodSchema),
  /** The provider's public key, for the card form. Null when the provider has none. */
  publicKey: z.string().nullable(),
  /** The BRL price of each credit pack. */
  creditPacks: z.array(CreditPackSchema),
});
export type PaymentConfig = z.infer<typeof PaymentConfigSchema>;

/**
 * The BRL price of a pack of model credit. The rate is the deployment's own configured price,
 * never a live quote, so nothing on the hot path waits on a currency service.
 */
export function creditPackBrlCents(usdCents: number, brlCentsPerUsd: number): number {
  if (!Number.isSafeInteger(usdCents) || usdCents <= 0) return 0;
  if (!Number.isSafeInteger(brlCentsPerUsd) || brlCentsPerUsd <= 0) return 0;
  return Math.round((usdCents / 100) * brlCentsPerUsd);
}

/** A PIX charge: the QR code a Brazilian banking app reads, and when it stops working. */
export const PixChargeSchema = z.object({
  id: Id,
  provider: z.string(),
  status: PaymentChargeStatusSchema,
  method: z.literal("pix"),
  amountCents: z.number().int().positive(),
  currency: z.literal("brl"),
  /** The BR Code payload, the string a payer copies and pastes into their bank app. */
  qrCode: z.string().min(1),
  /** The same code as a PNG data URL, when the provider renders one. */
  qrCodeBase64: z.string().nullable(),
  expiresAt: IsoDate,
  paidAt: IsoDate.nullable(),
  createdAt: IsoDate,
});
export type PixCharge = z.infer<typeof PixChargeSchema>;

/** A domestic card charge. Only the brand and the last four digits are ever kept. */
export const CardChargeSchema = z.object({
  id: Id,
  provider: z.string(),
  status: PaymentChargeStatusSchema,
  method: z.literal("credit_card"),
  amountCents: z.number().int().positive(),
  currency: z.literal("brl"),
  installments: z.number().int().positive(),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
  paidAt: IsoDate.nullable(),
  createdAt: IsoDate,
});
export type CardCharge = z.infer<typeof CardChargeSchema>;

/** A recurring monthly plan. The invoice is what actually charges the customer. */
export const PaymentSubscriptionStatusSchema = z.enum([
  "pending",
  "active",
  "paused",
  "past_due",
  "canceled",
]);
export type PaymentSubscriptionStatus = z.infer<typeof PaymentSubscriptionStatusSchema>;

export const PaymentSubscriptionSchema = z.object({
  id: Id,
  provider: z.string(),
  status: PaymentSubscriptionStatusSchema,
  amountCents: z.number().int().positive(),
  currency: z.literal("brl"),
  /** When the next monthly invoice is due. */
  nextBillingAt: IsoDate.nullable(),
  currentPeriodEndsAt: IsoDate.nullable(),
  canceledAt: IsoDate.nullable(),
  createdAt: IsoDate,
});
export type PaymentSubscription = z.infer<typeof PaymentSubscriptionSchema>;

/** One monthly invoice of a subscription. */
export const PaymentInvoiceSchema = z.object({
  id: Id,
  subscriptionId: Id,
  status: z.enum(["pending", "paid", "canceled", "failed"]),
  amountCents: z.number().int().positive(),
  currency: z.literal("brl"),
  issuedAt: IsoDate,
  dueAt: IsoDate.nullable(),
  paidAt: IsoDate.nullable(),
});
export type PaymentInvoice = z.infer<typeof PaymentInvoiceSchema>;

/** The largest charge a PIX payment may carry, in BRL centavos. */
export const MAX_PAYMENT_CENTS = 5_000_000;

export const PaymentAmountCentsSchema = z.number().int().positive().max(MAX_PAYMENT_CENTS);

/** A card token from the provider's own front-end. The number never reaches the server. */
export const CardTokenSchema = z
  .string()
  .trim()
  .min(6)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, "Use the provider's card token");
